<script setup>
  import {computed, inject, nextTick, onBeforeUnmount, onMounted, provide, ref, toValue, watch} from "vue";

  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentRecordCaseCloseDialog from "@components/Dialogs/RecordCaseCloseDialog.vue";
  import ComponentRecordInvalidatedDialog from "@components/Dialogs/RecordInvalidatedDialog.vue";
  import ComponentRecordShareDialog from "@components/Dialogs/RecordShareDialog.vue";
  import ComponentRecordShareStatusDialog from "@components/Dialogs/RecordShareStatusDialog.vue";
  import ComponentPatientSidebar from "@components/Sidebars/PatientSidebar.vue";
  import SkeletonLoader from "@components/SkeletonLoader.vue";
  import {Deferred as InertiaDeferred, router, useForm, usePage} from "@inertiajs/vue3";
  import PagesRecordGeneralShareStatus from "@pages/Records/Components/General/ShareStatus.vue";
  import ComponentRecordProgress from "@pages/Records/Components/RecordProgress.vue";
  import PagesRecordResultAddresses from "@pages/Records/Components/Result/Addresses.vue";
  import {analysisTypeGroupedByCheckId, area} from "@pages/Records/Components/Result/Enums.js";
  import PagesRecordResultHead from "@pages/Records/Components/Result/Head.vue";
  import PagesRecordResultMedicationTable from "@pages/Records/Components/Result/MedicationTable.vue";
  import PagesRecordResultNextSteps from "@pages/Records/Components/Result/NextSteps.vue";
  import PagesRecordResultNonComplianceResults from "@pages/Records/Components/Result/NonComplianceResults.vue";
  import PagesRecordResultOverview from "@pages/Records/Components/Result/Overview.vue";
  import PagesRecordResultPatientData from "@pages/Records/Components/Result/PatientData.vue";
  import PagesRecordResultPreventionResults from "@pages/Records/Components/Result/PreventionResults.vue";
  import PagesRecordResultPrognosticResults from "@pages/Records/Components/Result/PrognosticResults.vue";
  import PagesRecordResultListFilter from "@pages/Records/Components/Result/ResultList/Filter.vue";
  import PagesRecordResultSummary from "@pages/Records/Components/Result/ResultSummary.vue";
  import PagesRecordResultSummaryForPrint from "@pages/Records/Components/Result/ResultSummaryForPrint.vue";
  import PagesRecordResultSignature from "@pages/Records/Components/Result/Signature.vue";
  import PagesRecordResultSymptomaticResults from "@pages/Records/Components/Result/SymptomaticResults.vue";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {clone, debounce} from "es-toolkit";

  defineOptions({
    name: "PagesRecordShow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    medications: {
      type: Object,
      default: null,
    },
    symptoms: {
      type: Object,
      default: null,
    },
    diseases: {
      type: Object,
      default: null,
    },
    laborvalues: {
      type: Object,
      default: null,
    },
    checks: {
      type: Object,
      default: null,
    },
    transfer: {
      type: Object,
      default: null,
    },
    sharedata: {
      type: Object,
      default: null,
    },
    notifications: {
      type: Object,
      default: null,
    },
    comments: {
      type: Object,
      default: null,
    },
    hasAiPrioritizationResult: {
      type: Boolean,
      default: null,
    },
  });

  const privacy = inject("$privacy");
  const broadcast = inject("$broadcast");
  const pageProperties = usePage().props;

  // "replace" props.patient with a decrypted patient object
  const decryptedPatient = ref(null);
  const scrollPosition = ref(null);
  const stashedResultConfig = ref(null);
  const refShareDialog = ref(null);
  const triggerCloseClaim = ref(null);
  const refShareStatusDialog = ref(null);
  const refRecordCaseCloseDialog = ref(null);
  const activeComments = ref([]);
  const sharedWithMe = ref(false);
  const form = useForm({});
  const resultConfig = ref({
    analysistype: props.record.analysistype ?? "2a",
    veryhighrisk: true,
    highrisk: true,
    midrisk: true,
    lowrisk: true,
    showNoncomplianceAndPrevention: true,
    showCommunicationDoc: false,
    showCommunicationPatient: false,
    showAiPrioritizedClaims: false,
    aiPrioritizedClaims: [],
    defaultSetting: true,
    ...props.record?.metadata?.resultconfig,
  });
  const temporaryConfig = ref({
    printWithDoctorFeedback: true,
  });

  watch(
    () => props.comments,
    (newValue) => {
      activeComments.value = newValue?.data ?? [];
    },
  );

  watch(
    () => props.hasAiPrioritizationResult,
    (hasResult) => {
      // when the backend tells us there is no cached prioritization for the
      // current record/variant state, drop any stale prioritization that
      // might still be sitting in the record metadata
      if (hasResult === false) {
        resultConfig.value.aiPrioritizedClaims = [];
        resultConfig.value.showAiPrioritizedClaims = false;
      }
    },
    {immediate: true},
  );

  onMounted(async () => {
    decryptedPatient.value = await privacy.decryptPatient(props.patient);

    broadcast.patient.onMessage(props.patient.id, handleBroadcastMessage);
    broadcast.record.onMessage(props.record.id, handleBroadcastMessage);
  });

  onBeforeUnmount(() => {
    broadcast.patient.offMessage(props.patient.id, handleBroadcastMessage);
    broadcast.record.offMessage(props.record.id, handleBroadcastMessage);
  });

  const sumCommunicationDoctor = computed(() => {
    return (props?.checks?.data ?? []).reduce((totalSum, check) => {
      return (
        totalSum +
        check.claims.reduce((sum, claim) => {
          return sum + (claim.type === "redlight" && claim?.communicationdoc ? 1 : 0);
        }, 0)
      );
    }, 0);
  });

  const sumCommunicationPatient = computed(() => {
    return (props?.checks?.data ?? []).reduce((totalSum, check) => {
      return (
        totalSum +
        check.claims.reduce((sum, claim) => {
          return sum + (claim.type === "redlight" && claim?.communicationpatient ? 1 : 0);
        }, 0)
      );
    }, 0);
  });

  const greenlights = computed(() => {
    return (props?.checks?.data ?? []).filter(
      (check) => check.contains_redlight_claim === false && _filterCheckByResultConfig(check),
    );
  });

  const symptomaticRedlights = computed(() => {
    return (
      (props?.checks?.data ?? [])
        .filter(
          (check) =>
            check.contains_redlight_claim === true &&
            (check.group === "symptomatic" || check.group === "mixed") &&
            _filterCheckByResultConfig(check),
        )
        // filter the nested checks according to resultConfig
        .map((check) => {
          return {
            ...check,
            claims: _filterClaimsByResultConfig(check.claims).filter((claim) => claim.area === area.symptomatic),
          };
        })
        // now filter checks without any claims left
        .filter((check) => check.claims.length > 0)
    );
  });

  const prognosticRedlights = computed(() => {
    return (
      (props?.checks?.data ?? [])
        .filter(
          (check) =>
            check.contains_redlight_claim === true &&
            (check.group === "prognostic" || check.group === "mixed") &&
            _filterCheckByResultConfig(check),
        )
        // filter the nested checks according to resultConfig
        .map((check) => {
          return {
            ...check,
            claims: _filterClaimsByResultConfig(check.claims).filter((claim) => claim.area === area.prognostic),
          };
        })
        // now filter checks without any claims left
        .filter((check) => check.claims.length > 0)
    );
  });

  const nonComplianceRedlights = computed(() => {
    // early filter according to resultConfig
    if (!resultConfig.value.showNoncomplianceAndPrevention) {
      return [];
    }

    return (
      (props?.checks?.data ?? [])
        .filter(
          (check) =>
            check.contains_redlight_claim === true &&
            check.group === "non_compliance" &&
            _filterCheckByResultConfig(check),
        )
        // filter the nested checks according to resultConfig
        .map((check) => {
          return {
            ...check,
            claims: _filterClaimsByResultConfig(check.claims),
          };
        })
        // now filter checks without any claims left
        .filter((check) => check.claims.length > 0)
    );
  });

  const preventionRedlights = computed(() => {
    // early filter according to resultConfig
    if (!resultConfig.value.showNoncomplianceAndPrevention) {
      return [];
    }

    return (
      (props?.checks?.data ?? [])
        .filter(
          (check) =>
            check.contains_redlight_claim === true && check.group === "prevention" && _filterCheckByResultConfig(check),
        )
        // filter the nested checks according to resultConfig
        .map((check) => {
          return {
            ...check,
            claims: _filterClaimsByResultConfig(check.claims),
          };
        })
        // now filter checks without any claims left
        .filter((check) => check.claims.length > 0)
    );
  });

  if (
    !(
      pageProperties?.client?.users
        .find((user) => user.id === privacy.getCurrentUser()?.id)
        ?.configurations.find((configuration) => configuration.name === "doNotCloseClaimsAutomatically")?.amount ??
      false
    )
  ) {
    // if the user HAS NOT the doNotCloseClaimsAutomatically
    // configuration set to true, we provide a method to
    // close all claim siblings
    provide("closeClaimSiblings", (openClaim) => {
      triggerCloseClaim.value = openClaim;
    });
  } else {
    // otherwise provide an empty method to avoid "injection
    // not found" warnings
    provide("closeClaimSiblings", () => {});
  }

  watch(
    resultConfig,
    (newValue) => {
      // re-activate other filters when communication filter is active
      if (newValue.showCommunicationDoc || newValue.showCommunicationPatient) {
        resultConfig.value.veryhighrisk = true;
        resultConfig.value.highrisk = true;
        resultConfig.value.midrisk = true;
        resultConfig.value.lowrisk = true;
        resultConfig.value.showNoncomplianceAndPrevention = true;
      }

      // set default setting
      resultConfig.value.defaultSetting =
        newValue.analysistype === (props.record.analysistype ?? "2a") &&
        newValue.veryhighrisk &&
        newValue.highrisk &&
        newValue.midrisk &&
        newValue.lowrisk &&
        newValue.showNoncomplianceAndPrevention &&
        !newValue.showCommunicationDoc &&
        !newValue.showCommunicationPatient &&
        !newValue.showAiPrioritizedClaims;

      if (stashedResultConfig.value === null) {
        // save every change if not simulating the result config
        save();
      }
    },
    {deep: true},
  );

  const handleBroadcastMessage = (data) => {
    switch (data?.action) {
      case "refreshAfterReload.patient": {
        privacy.decryptPatient(props.patient).then((decrypted) => {
          decryptedPatient.value = decrypted;
        });
        break;
      }
      case "reload.record": {
        router.reload();
        break;
      }
    }
  };

  const save = debounce(function () {
    form
      .transform(() => {
        return {
          metadata: {
            resultconfig: resultConfig.value,
          },
        };
      })
      .patch(route("records.update", {patient: props.record.patient_id, record: props.record.id}), {
        preserveScroll: true,
        only: ["flash", "errors", "record"],
        headers: {
          // avoid updating the edited_at values when just changing the filter
          "Mc-Silent-Update": true,
        },
      });
  }, 2000);

  function _filterCheckByResultConfig(check) {
    return analysisTypeGroupedByCheckId[resultConfig.value.analysistype]?.indexOf(check.mc_check_id) >= 0;
  }

  function _filterClaimsByResultConfig(claims) {
    return claims.filter((claim) => {
      if (
        resultConfig.value.showAiPrioritizedClaims &&
        resultConfig.value.aiPrioritizedClaims &&
        !resultConfig.value.aiPrioritizedClaims.includes(claim.section)
      ) {
        return false;
      }

      if (!resultConfig.value.veryhighrisk && claim.class === "red") {
        return false;
      }
      if (!resultConfig.value.highrisk && claim.class === "orange") {
        return false;
      }
      if (!resultConfig.value.midrisk && claim.class === "yellow") {
        return false;
      }
      if (!resultConfig.value.lowrisk && (claim.class === "gray" || claim.class === "grey")) {
        return false;
      }

      if (
        resultConfig.value.showCommunicationDoc &&
        resultConfig.value.showCommunicationDoc &&
        !claim.communicationdoc &&
        !claim.communicationpatient
      ) {
        return false;
      }

      if (
        resultConfig.value.showCommunicationDoc &&
        !claim.communicationdoc &&
        !resultConfig.value.showCommunicationPatient
      ) {
        return false;
      }
      if (
        resultConfig.value.showCommunicationPatient &&
        !claim.communicationpatient &&
        !resultConfig.value.showCommunicationDoc
      ) {
        return false;
      }

      return true;
    });
  }

  function openShareDialog(initialTab) {
    refShareDialog.value.open(undefined, initialTab);
  }

  function openShareStatusDialog() {
    refShareStatusDialog.value.open();
  }

  function openRecordCaseCloseDialog() {
    refRecordCaseCloseDialog.value.open();
  }

  provide("sumCommunicationDoctor", sumCommunicationDoctor);

  provide("comments", activeComments);

  // provide sharedWithMe instead of passing it through to ClaimInteractionChat component
  provide("sharedWithMe", sharedWithMe);

  // Update sharedWithMe
  watch(
    () => props.sharedata,
    (newValue) => {
      if (newValue !== undefined) {
        sharedWithMe.value = props.sharedata?.shared_with_me ?? false;
      }
    },
    {immediate: true},
  );

  provide("sumCommunicationPatient", sumCommunicationPatient);

  provide("resultConfig", resultConfig);

  provide("resetDefaultResultConfig", () => {
    resultConfig.value.analysistype = props.record.analysistype ?? "2a";
    resultConfig.value.veryhighrisk = true;
    resultConfig.value.highrisk = true;
    resultConfig.value.midrisk = true;
    resultConfig.value.lowrisk = true;
    resultConfig.value.showNoncomplianceAndPrevention = true;
    resultConfig.value.showCommunicationDoc = false;
    resultConfig.value.showCommunicationPatient = false;
    resultConfig.value.showAiPrioritizedClaims = false;
    resultConfig.value.defaultSetting = true;
  });

  provide("simulateResultConfig", (key, value) => {
    if (stashedResultConfig.value === null) {
      stashedResultConfig.value = clone(resultConfig.value);
      // save the scroll position once (on the first call)
      if (scrollPosition.value === null) {
        scrollPosition.value = window.scrollY;
      }
    }
    resultConfig.value[key] = value;
  });

  provide("restoreResultConfig", () => {
    if (stashedResultConfig.value !== null) {
      resultConfig.value = clone(stashedResultConfig.value);
      nextTick(() => {
        // restore scroll position
        if (scrollPosition.value !== null) {
          window.scrollTo(0, scrollPosition.value);
          scrollPosition.value = null;
        }
        stashedResultConfig.value = null;
      });
    }
  });

  provide("triggerCloseClaim", triggerCloseClaim);

  provide("temporaryConfig", toValue(temporaryConfig));

  provide("setTemporaryConfig", (key, value) => {
    temporaryConfig.value[key] = value;
  });

  provide("resetTemporaryConfig", () => {
    temporaryConfig.value = {
      printWithDoctorFeedback: true,
    };
  });
</script>

<template>
  <template v-if="decryptedPatient">
    <ComponentBreadcrumb
      class="mt-2 print:hidden"
      :links="[
        {
          label: 'Patienten',
          url: patient.is_fake ? $route('patients.index', {filter: 'shared-with-me'}) : $route('patients.index'),
        },
        {
          encryptedLabel: [decryptedPatient.firstname, decryptedPatient.lastname],
          url: $route('patients.show', {patient: decryptedPatient.id}),
        },
        {
          label: 'Eingabe',
          subLabel: {
            component: 'component-record-label',
            props: {
              record: record,
            },
          },
          url: $route('records.edit', {patient: decryptedPatient.id, record: record.id}),
        },
        {
          label: 'Analyse',
        },
      ]"
    />

    <section
      :class="
        cn('relative mt-6 flex items-start space-x-6 print:mt-0 print:space-x-0', {
          'print-archive': !resultConfig.showCommunicationDoc && !resultConfig.showCommunicationPatient,
          'print-patient': resultConfig.showCommunicationPatient,
          'print-doc': resultConfig.showCommunicationDoc,
          'print-with-doctor-feedback': temporaryConfig.printWithDoctorFeedback,
        })
      "
    >
      <div class="sticky top-20 z-10 print:hidden">
        <InertiaDeferred :data="['transfer']">
          <template #fallback />
          <ComponentPatientSidebar :patient="decryptedPatient" :record="record" :transfer="transfer" />
        </InertiaDeferred>
      </div>

      <article class="flex-1 space-y-6">
        <!-- article::main -->
        <div
          class="w-10/12 max-w-(--breakpoint-xl) space-y-8 bg-white p-4 shadow-sm sm:w-11/12 print:w-full print:max-w-none print:p-0 print:shadow-none"
        >
          <div class="flex justify-end space-x-1 print:hidden">
            <span :data-test="getTestId('last-save')" class="m-auto mr-2 text-xs">
              Gespeichert:
              <span class="text-xs" v-text="record.updated_at_datetime" />
            </span>

            <InertiaDeferred data="transfer">
              <template #fallback>
                <SkeletonLoader class="size-8" />
                <SkeletonLoader class="size-8" />
                <SkeletonLoader class="size-8" />
              </template>

              <ComponentRecordProgress
                :record="record"
                :transfer="transfer"
                :is-on-result-page="true"
                @open:record-case-close-dialog="openRecordCaseCloseDialog"
              />
            </InertiaDeferred>
          </div>

          <InertiaDeferred data="sharedata">
            <template #fallback>
              <SkeletonLoader class="h-32 w-full" />
            </template>

            <PagesRecordResultHead :record="record" :sharedata="sharedata" :test-id="getTestId()" />
          </InertiaDeferred>

          <InertiaDeferred data="transfer">
            <template #fallback>
              <div class="flex space-x-8">
                <SkeletonLoader class="h-60 w-full" />
                <SkeletonLoader class="h-60 w-full" />
              </div>
            </template>

            <PagesRecordResultAddresses
              :patient="decryptedPatient"
              :record="record"
              :transfer="transfer"
              :test-id="getTestId()"
            />
          </InertiaDeferred>

          <InertiaDeferred data="sharedata">
            <template #fallback />
            <PagesRecordGeneralShareStatus
              v-if="$page.props.client.license.is_pro"
              :patient="decryptedPatient"
              :record="record"
              :sharedata="sharedata"
              @open:share-status-dialog="openShareStatusDialog()"
            />
          </InertiaDeferred>

          <InertiaDeferred :data="['symptoms', 'diseases', 'laborvalues']">
            <template #fallback>
              <SkeletonLoader class="h-60 w-full" />
            </template>

            <PagesRecordResultPatientData
              :patient="decryptedPatient"
              :record="record"
              :symptoms="symptoms.data"
              :diseases="diseases.data"
              :laborvalues="laborvalues.data"
              :test-id="getTestId()"
            />
          </InertiaDeferred>

          <InertiaDeferred data="medications">
            <template #fallback>
              <SkeletonLoader class="h-60 w-full" />
            </template>

            <PagesRecordResultMedicationTable :record="record" :medications="medications.data" :test-id="getTestId()" />
          </InertiaDeferred>

          <PagesRecordResultSummaryForPrint :record="record" />

          <InertiaDeferred data="checks">
            <template #fallback>
              <div class="flex space-x-8">
                <SkeletonLoader class="h-60 w-full bg-mcgreen-100" />
                <SkeletonLoader class="h-40 w-full bg-mcred-100" />
              </div>
            </template>

            <PagesRecordResultListFilter
              :record="record"
              :patient="decryptedPatient"
              :checks="checks?.data ?? []"
              :test-id="getTestId()"
            />

            <PagesRecordResultOverview
              :record="record"
              :greenlights="greenlights"
              :symptomatic-redlights="symptomaticRedlights"
              :prognostic-redlights="prognosticRedlights"
              :non-compliance-redlights="nonComplianceRedlights"
              :prevention-redlights="preventionRedlights"
              :test-id="getTestId()"
            />

            <PagesRecordResultSymptomaticResults
              :patient-id="decryptedPatient.id"
              :record-id="record.id"
              :symptoms="symptoms?.data ?? []"
              :symptomatic-redlights="symptomaticRedlights"
              :test-id="getTestId()"
              @open:share-dialog="openShareDialog($event)"
            />

            <PagesRecordResultPrognosticResults
              :patient-id="decryptedPatient.id"
              :record-id="record.id"
              :prognostic-redlights="prognosticRedlights"
              :test-id="getTestId()"
              @open:share-dialog="openShareDialog($event)"
            />

            <PagesRecordResultNonComplianceResults
              :patient-id="decryptedPatient.id"
              :record-id="record.id"
              :non-compliance-redlights="nonComplianceRedlights"
              :test-id="getTestId()"
              @open:share-dialog="openShareDialog($event)"
            />

            <PagesRecordResultPreventionResults
              :patient-id="decryptedPatient.id"
              :record-id="record.id"
              :prevention-redlights="preventionRedlights"
              :test-id="getTestId()"
              @open:share-dialog="openShareDialog($event)"
            />
          </InertiaDeferred>

          <PagesRecordResultSummary
            :record="record"
            :checks="checks?.data ?? []"
            :patient="decryptedPatient"
            :test-id="getTestId()"
            @open:share-dialog="openShareDialog($event)"
          />

          <PagesRecordResultSignature :record="record" :test-id="getTestId()" />
        </div>

        <div class="w-10/12 max-w-(--breakpoint-xl) space-y-16 bg-white p-4 shadow-sm sm:w-11/12 print:hidden">
          <InertiaDeferred :data="['transfer', 'sharedata']">
            <template #fallback>
              <SkeletonLoader class="h-40 w-full" />
            </template>

            <PagesRecordResultNextSteps
              :patient="decryptedPatient"
              :record="record"
              :transfer="transfer"
              :sharedata="sharedata"
              :test-id="getTestId()"
              @open:share-dialog="openShareDialog($event)"
              @open:share-status-dialog="openShareStatusDialog()"
              @open:record-case-close-dialog="openRecordCaseCloseDialog"
            />
          </InertiaDeferred>
        </div>
      </article>

      <InertiaDeferred :data="['laborvalues']">
        <template #fallback />
        <ComponentRecordShareDialog
          ref="refShareDialog"
          :record="record"
          :sharedata="sharedata"
          :patient="decryptedPatient"
          :laborvalues="laborvalues"
          test-id="record-show"
        />
      </InertiaDeferred>

      <InertiaDeferred :data="['sharedata', 'notifications']">
        <template #fallback />
        <ComponentRecordShareStatusDialog
          ref="refShareStatusDialog"
          :patient="decryptedPatient"
          :record="record"
          :sharedata="sharedata"
          :notifications="notifications?.data"
        />
      </InertiaDeferred>

      <InertiaDeferred :data="['transfer', 'medications', 'laborvalues', 'diseases']">
        <template #fallback />
        <ComponentRecordCaseCloseDialog
          ref="refRecordCaseCloseDialog"
          :patient="decryptedPatient"
          :record="record"
          :diseases="diseases"
          :laborvalues="laborvalues"
          :medications="medications"
          :transfer="transfer"
          :test-id="getTestId()"
        />
      </InertiaDeferred>

      <ComponentRecordInvalidatedDialog :record="record" />
    </section>
  </template>
</template>
